import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, Title, Tooltip, Legend, PointElement, Filler } from "chart.js";
import { DateTime } from "luxon";
import { Badge } from "@radix-ui/themes";
import { useTheme } from "../../context/ThemeContext";
import "./ChartColours.css";

ChartJS.register(CategoryScale, LinearScale, LineElement, Title, Tooltip, Legend, PointElement, Filler);

const getCSSVariable = (variable) => getComputedStyle(document.documentElement).getPropertyValue(variable).trim();

export const AverageDurationByDayChart = ({ data, startTimeMilliseconds, endTimeMilliseconds, timezone, displayBy }) => {
  const { appearance } = useTheme();
  const [fontColor, setFontColor] = useState("");
  const [chartNeutralColour, setChartNeutralColour] = useState("");
  const [chartNeutralBorderColour, setChartNeutralBorderColour] = useState("");
  const [gridColour, setGridColour] = useState("");
  const [chartPointsBgColour, setChartPointsBgColour] = useState("");
  const [chartContainerRef, setChartContainerRef] = useState(null);
  const [badgePosition, setBadgePosition] = useState({
    left: 0,
    top: 0,
    display: "none",
  });
  const [chartNewConnectionColour, setChartNewConnectionColour] = useState("");
  const [chartReturningConnectionColour, setChartReturningConnectionColour] = useState("");
  const [chartAgeNoResponseColour, setChartAgeNoResponseColour] = useState("");
  const [chartAgeOver65Colour, setChartAgeOver65Colour] = useState("");
  const [chartAge5564Colour, setChartAge5564Colour] = useState("");
  const [chartAge4554Colour, setChartAge4554Colour] = useState("");
  const [chartAge3544Colour, setChartAge3544Colour] = useState("");
  const [chartAge2534Colour, setChartAge2534Colour] = useState("");
  const [chartAge1824Colour, setChartAge1824Colour] = useState("");
  const [chartAgeBelow18Colour, setChartAgeBelow18Colour] = useState("");
  const [chartGenderMaleColour, setChartGenderMaleColour] = useState("");
  const [chartGenderFemaleColour, setChartGenderFemaleColour] = useState("");
  const [chartGenderOtherColour, setChartGenderOtherColour] = useState("");
  const [chartGenderNoResponseColour, setChartGenderNoResponseColour] = useState("");
  const [chartNewConnectionBorderColour, setChartNewConnectionBorderColour] = useState("");
  const [chartReturningConnectionBorderColour, setChartReturningConnectionBorderColour] = useState("");
  const [chartAgeNoResponseBorderColour, setChartAgeNoResponseBorderColour] = useState("");
  const [chartAgeOver65BorderColour, setChartAgeOver65BorderColour] = useState("");
  const [chartAge5564BorderColour, setChartAge5564BorderColour] = useState("");
  const [chartAge4554BorderColour, setChartAge4554BorderColour] = useState("");
  const [chartAge3544BorderColour, setChartAge3544BorderColour] = useState("");
  const [chartAge2534BorderColour, setChartAge2534BorderColour] = useState("");
  const [chartAge1824BorderColour, setChartAge1824BorderColour] = useState("");
  const [chartAgeBelow18BorderColour, setChartAgeBelow18BorderColour] = useState("");
  const [chartGenderMaleBorderColour, setChartGenderMaleBorderColour] = useState("");
  const [chartGenderFemaleBorderColour, setChartGenderFemaleBorderColour] = useState("");
  const [chartGenderOtherBorderColour, setChartGenderOtherBorderColour] = useState("");
  const [chartGenderNoResponseBorderColour, setChartGenderNoResponseBorderColour] = useState("");

  useEffect(() => {
    const fontColourLight = getCSSVariable("--chart-font-colour-light");
    const fontColourDark = getCSSVariable("--chart-font-colour-dark");
    const positiveColour = getCSSVariable("--chart-positive-colour");
    const gridColourLight = getCSSVariable("--chart-grid-colour-light");
    const gridColourDark = getCSSVariable("--chart-grid-colour-dark");
    const chartPointsBgColourLight = getCSSVariable("--chart-points-bg-colour-light");
    const chartPointsBgColourDark = getCSSVariable("--chart-points-bg-colour-dark");
    const chartNeutralColour = getCSSVariable("--chart-neutral-colour");
    const chartNeutralBorderColour = getCSSVariable("--chart-neutral-border-colour");
    const chartNeutralColourDark = getCSSVariable("--chart-neutral-colour-dark");
    const chartNeutralBorderColourDark = getCSSVariable("--chart-neutral-border-colour-dark");
    const chartNewConnectionColour = getCSSVariable("--chart-new-connection-colour");
    const chartReturningConnectionColour = getCSSVariable("--chart-returning-connection-colour");
    const chartAgeNoResponseColour = getCSSVariable("--chart-age-no-response-colour");
    const chartAgeOver65Colour = getCSSVariable("--chart-age-over-65-colour");
    const chartAge5564Colour = getCSSVariable("--chart-age-55-64-colour");
    const chartAge4554Colour = getCSSVariable("--chart-age-45-54-colour");
    const chartAge3544Colour = getCSSVariable("--chart-age-35-44-colour");
    const chartAge2534Colour = getCSSVariable("--chart-age-25-34-colour");
    const chartAge1824Colour = getCSSVariable("--chart-age-18-24-colour");
    const chartAgeBelow18Colour = getCSSVariable("--chart-age-below-18-colour");
    const chartGenderMaleColour = getCSSVariable("--chart-gender-male-colour");
    const chartGenderFemaleColour = getCSSVariable("--chart-gender-female-colour");
    const chartGenderOtherColour = getCSSVariable("--chart-gender-other-colour");
    const chartGenderNoResponseColour = getCSSVariable("--chart-gender-no-response-colour");
    const chartNewConnectionBorderColour = getCSSVariable("--chart-new-connection-border-colour");
    const chartReturningConnectionBorderColour = getCSSVariable("--chart-returning-connection-border-colour");
    const chartNewConnectionBorderColourDark = getCSSVariable("--chart-new-connection-border-colour-dark");
    const chartReturningConnectionBorderColourDark = getCSSVariable("--chart-returning-connection-border-colour-dark");
    const chartNewConnectionColourDark = getCSSVariable("--chart-new-connection-colour-dark");
    const chartReturningConnectionColourDark = getCSSVariable("--chart-returning-connection-colour-dark");
    const chartAgeNoResponseBorderColour = getCSSVariable("--chart-age-no-response-border-colour");
    const chartAgeOver65BorderColour = getCSSVariable("--chart-age-over-65-border-colour");
    const chartAge5564BorderColour = getCSSVariable("--chart-age-55-64-border-colour");
    const chartAge4554BorderColour = getCSSVariable("--chart-age-45-54-border-colour");
    const chartAge3544BorderColour = getCSSVariable("--chart-age-35-44-border-colour");
    const chartAge2534BorderColour = getCSSVariable("--chart-age-25-34-border-colour");
    const chartAge1824BorderColour = getCSSVariable("--chart-age-18-24-border-colour");
    const chartAgeBelow18BorderColour = getCSSVariable("--chart-age-below-18-border-colour");
    const chartAgeBelow18BorderColourDark = getCSSVariable("--chart-age-below-18-border-colour-dark");
    const chartAge1824BorderColourDark = getCSSVariable("--chart-age-18-24-border-colour-dark");
    const chartAge2534BorderColourDark = getCSSVariable("--chart-age-25-34-border-colour-dark");
    const chartAge3544BorderColourDark = getCSSVariable("--chart-age-35-44-border-colour-dark");
    const chartAge4554BorderColourDark = getCSSVariable("--chart-age-45-54-border-colour-dark");
    const chartAge5564BorderColourDark = getCSSVariable("--chart-age-55-64-border-colour-dark");
    const chartAgeOver65BorderColourDark = getCSSVariable("--chart-age-over-65-border-colour-dark");
    const chartAgeNoResponseBorderColourDark = getCSSVariable("--chart-age-no-response-border-colour-dark");
    const chartAgeNoResponseColourDark = getCSSVariable("--chart-age-no-response-colour-dark");
    const chartAgeOver65ColourDark = getCSSVariable("--chart-age-over-65-colour-dark");
    const chartAge5564ColourDark = getCSSVariable("--chart-age-55-64-colour-dark");
    const chartAge4554ColourDark = getCSSVariable("--chart-age-45-54-colour-dark");
    const chartAge3544ColourDark = getCSSVariable("--chart-age-35-44-colour-dark");
    const chartAge2534ColourDark = getCSSVariable("--chart-age-25-34-colour-dark");
    const chartAge1824ColourDark = getCSSVariable("--chart-age-18-24-colour-dark");
    const chartAgeBelow18ColourDark = getCSSVariable("--chart-age-below-18-colour-dark");
    const chartGenderMaleBorderColour = getCSSVariable("--chart-gender-male-border-colour");
    const chartGenderFemaleBorderColour = getCSSVariable("--chart-gender-female-border-colour");
    const chartGenderOtherBorderColour = getCSSVariable("--chart-gender-other-border-colour");
    const chartGenderNoResponseBorderColour = getCSSVariable("--chart-gender-no-response-border-colour");
    const chartGenderMaleBorderColourDark = getCSSVariable("--chart-gender-male-border-colour-dark");
    const chartGenderFemaleBorderColourDark = getCSSVariable("--chart-gender-female-border-colour-dark");
    const chartGenderOtherBorderColourDark = getCSSVariable("--chart-gender-other-border-colour-dark");
    const chartGenderNoResponseBorderColourDark = getCSSVariable("--chart-gender-no-response-border-colour-dark");
    const chartGenderMaleColourDark = getCSSVariable("--chart-gender-male-colour-dark");
    const chartGenderFemaleColourDark = getCSSVariable("--chart-gender-female-colour-dark");
    const chartGenderOtherColourDark = getCSSVariable("--chart-gender-other-colour-dark");
    const chartGenderNoResponseColourDark = getCSSVariable("--chart-gender-no-response-colour-dark");

    if (appearance === "dark") {
      setFontColor(fontColourDark);
      setGridColour(gridColourDark);
      setChartPointsBgColour(chartPointsBgColourDark);
      setChartNeutralColour(chartNeutralColourDark);
      setChartNeutralBorderColour(chartNeutralBorderColourDark);
      setChartNewConnectionColour(chartNewConnectionColourDark);
      setChartReturningConnectionColour(chartReturningConnectionColourDark);
      setChartNewConnectionBorderColour(chartNewConnectionBorderColourDark);
      setChartReturningConnectionBorderColour(chartReturningConnectionBorderColourDark);
      setChartAgeNoResponseColour(chartAgeNoResponseColourDark);
      setChartAgeOver65Colour(chartAgeOver65ColourDark);
      setChartAge5564Colour(chartAge5564ColourDark);
      setChartAge4554Colour(chartAge4554ColourDark);
      setChartAge3544Colour(chartAge3544ColourDark);
      setChartAge2534Colour(chartAge2534ColourDark);
      setChartAge1824Colour(chartAge1824ColourDark);
      setChartAgeBelow18Colour(chartAgeBelow18ColourDark);
      setChartAgeNoResponseBorderColour(chartAgeNoResponseBorderColourDark);
      setChartAgeOver65BorderColour(chartAgeOver65BorderColourDark);
      setChartAge5564BorderColour(chartAge5564BorderColourDark);
      setChartAge4554BorderColour(chartAge4554BorderColourDark);
      setChartAge3544BorderColour(chartAge3544BorderColourDark);
      setChartAge2534BorderColour(chartAge2534BorderColourDark);
      setChartAge1824BorderColour(chartAge1824BorderColourDark);
      setChartAgeBelow18BorderColour(chartAgeBelow18BorderColourDark);
      setChartGenderMaleColour(chartGenderMaleColourDark);
      setChartGenderFemaleColour(chartGenderFemaleColourDark);
      setChartGenderOtherColour(chartGenderOtherColourDark);
      setChartGenderNoResponseColour(chartGenderNoResponseColourDark);
      setChartGenderMaleBorderColour(chartGenderMaleBorderColourDark);
      setChartGenderFemaleBorderColour(chartGenderFemaleBorderColourDark);
      setChartGenderOtherBorderColour(chartGenderOtherBorderColourDark);
      setChartGenderNoResponseBorderColour(chartGenderNoResponseBorderColourDark);
    } else {
      setFontColor(fontColourLight);
      setGridColour(gridColourLight);
      setChartPointsBgColour(chartPointsBgColourLight);
      setChartNeutralColour(chartNeutralColour);
      setChartNeutralBorderColour(chartNeutralBorderColour);
      setChartNewConnectionColour(chartNewConnectionColour);
      setChartReturningConnectionColour(chartReturningConnectionColour);
      setChartNewConnectionBorderColour(chartNewConnectionBorderColour);
      setChartReturningConnectionBorderColour(chartReturningConnectionBorderColour);
      setChartAgeNoResponseColour(chartAgeNoResponseColour);
      setChartAgeOver65Colour(chartAgeOver65Colour);
      setChartAge5564Colour(chartAge5564Colour);
      setChartAge4554Colour(chartAge4554Colour);
      setChartAge3544Colour(chartAge3544Colour);
      setChartAge2534Colour(chartAge2534Colour);
      setChartAge1824Colour(chartAge1824Colour);
      setChartAgeBelow18Colour(chartAgeBelow18Colour);
      setChartAgeNoResponseBorderColour(chartAgeNoResponseBorderColour);
      setChartAgeOver65BorderColour(chartAgeOver65BorderColour);
      setChartAge5564BorderColour(chartAge5564BorderColour);
      setChartAge4554BorderColour(chartAge4554BorderColour);
      setChartAge3544BorderColour(chartAge3544BorderColour);
      setChartAge2534BorderColour(chartAge2534BorderColour);
      setChartAge1824BorderColour(chartAge1824BorderColour);
      setChartAgeBelow18BorderColour(chartAgeBelow18BorderColour);
      setChartGenderMaleColour(chartGenderMaleColour);
      setChartGenderFemaleColour(chartGenderFemaleColour);
      setChartGenderOtherColour(chartGenderOtherColour);
      setChartGenderNoResponseColour(chartGenderNoResponseColour);
      setChartGenderMaleBorderColour(chartGenderMaleBorderColour);
      setChartGenderFemaleBorderColour(chartGenderFemaleBorderColour);
      setChartGenderOtherBorderColour(chartGenderOtherBorderColour);
      setChartGenderNoResponseBorderColour(chartGenderNoResponseBorderColour);
    }
  }, [appearance]);

  const generateBins = (startMs, endMs) => {
    const bins = [];
    let cur = DateTime.fromMillis(startMs, { zone: timezone }).startOf('day');
    while (cur.toMillis() < endMs) {
      const binStart = cur.toMillis();
      const binEnd = cur.plus({ days: 1 }).startOf('day').toMillis();
      bins.push([binStart, binEnd]);
      cur = cur.plus({ days: 1 }).startOf('day');
    }
    return bins;
  };
  const findBinIndex = (bins, t) => {
    let lo = 0, hi = bins.length - 1;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      const [b0, b1] = bins[mid];
      if (b0 <= t && t < b1) return mid;
      if (t < b0) hi = mid - 1;
      else lo = mid + 1;
    }
    return -1;
  };

  const bar_chart_bins = generateBins(startTimeMilliseconds, endTimeMilliseconds);

  // find today's bin for badge
  const today = DateTime.now().startOf("day");
  const todayIndex = bar_chart_bins.findIndex(([s]) =>
    DateTime.fromMillis(s).hasSame(today, "day")
  );
  const isCurrentDayInRange = () => {
    const endDate = DateTime.fromMillis(endTimeMilliseconds).setZone(timezone);
    return DateTime.now().setZone(timezone).hasSame(endDate, "day");
  };

  const formatXAxisLabel = (value) => {
    // Add null check and default value
    if (!value) {
      return "";
    }

    try {
      // Convert to number to ensure numerical input
      const timestamp = Number(value);

      // Validate timestamp
      if (isNaN(timestamp)) {
        return "";
      }

      // Format the date
      return DateTime.fromMillis(timestamp).toFormat("dd MMM");
    } catch (error) {
      console.warn("Error formatting x-axis label:", error);
      return "";
    }
  };

  let processed_chart_data = bar_chart_bins.map(([s, e]) => ({
    start_date: s,
    end_date: e,
    totalDuration: 0,   // ← will accumulate minutes
    count: 0,   // ← will accumulate number of sessions
    average: 0,
    guest: {
      new: { totalDuration: 0, count: 0, average: 0 },
      returning: { totalDuration: 0, count: 0, average: 0 },
    },
    total: 0,
    age: {
      "Under 18": { totalDuration: 0, count: 0, average: 0 },
      "18-24": { totalDuration: 0, count: 0, average: 0 },
      "25-34": { totalDuration: 0, count: 0, average: 0 },
      "35-44": { totalDuration: 0, count: 0, average: 0 },
      "45-54": { totalDuration: 0, count: 0, average: 0 },
      "55-64": { totalDuration: 0, count: 0, average: 0 },
      "65+": { totalDuration: 0, count: 0, average: 0 },
      no_response: { totalDuration: 0, count: 0, average: 0 },
    },
    gender: {
      male: { totalDuration: 0, count: 0, average: 0 },
      female: { totalDuration: 0, count: 0, average: 0 },
      others: { totalDuration: 0, count: 0, average: 0 },
      no_response: { totalDuration: 0, count: 0, average: 0 },
    },
  }));

  if (data?.data?.connection_details?.length) {
    for (const ev of data.data.connection_details) {
      let dt0 = DateTime
        .fromISO(ev.connection_start_time, { setZone: true })
        .setZone(timezone, { keepLocalTime: false });

      const dt1 = DateTime
        .fromISO(ev.connection_end_time,   { setZone: true })
        .setZone(timezone, { keepLocalTime: false });

      while (dt0 < dt1) {
        const nextMidnight = dt0.plus({ days: 1 }).startOf('day');
        const chunkEnd     = dt1 < nextMidnight ? dt1 : nextMidnight;
        const minutes      = chunkEnd.diff(dt0, 'minutes').minutes;
        const idx          = findBinIndex(bar_chart_bins, dt0.toMillis());

        if (idx !== -1) {
          processed_chart_data[idx].totalDuration += minutes;
          processed_chart_data[idx].count++;
          const statusKey = ev.guest_status === 'returning' ? 'returning' : 'new';
          processed_chart_data[idx].guest[statusKey].totalDuration += minutes;
          processed_chart_data[idx].guest[statusKey].count++;
          const ageKey = ev.age === 'field_disabled' || !ev.age ? 'no_response' : ev.age;
          processed_chart_data[idx].age[ageKey].totalDuration += minutes;
          processed_chart_data[idx].age[ageKey].count++;
          const genderKey = ev.gender === 'field_disabled' || !ev.gender ? 'no_response' : ev.gender;
          processed_chart_data[idx].gender[genderKey].totalDuration += minutes;
          processed_chart_data[idx].gender[genderKey].count++;
        }

        dt0 = chunkEnd;
      }
    }


    processed_chart_data.forEach(bin => {
      bin.average = bin.count > 0 ? Math.round(bin.totalDuration / bin.count) : 0;

      ['new', 'returning'].forEach(status => {
        const g = bin.guest[status];
        g.average = g.count > 0 ? Math.round(g.totalDuration / g.count) : 0;
      });

      ['Under 18', '18-24', '25-34', '35-44', '45-54', '55-64', '65+', 'no_response'].forEach(ageKey => {
        const a = bin.age[ageKey];
        a.average = a.count > 0 ? Math.round(a.totalDuration / a.count) : 0;
      });

      ['male', 'female', 'others', 'no_response'].forEach(gender => {
        const g = bin.gender[gender];
        g.average = g.count > 0 ? Math.round(g.totalDuration / g.count) : 0;
      });
    });
  }

  const labels = processed_chart_data.map(bin =>
    DateTime.fromMillis(bin.start_date).toFormat("dd MMM")
  );

  const hasNonZeroData = (data) => {
    return data.some((value) => value > 0);
  };

  const chartData = {
    labels,
    datasets:
      displayBy === "guest_status"
        ? [
          {
            label: "Returning",
            data: processed_chart_data.map(bin => bin.guest.returning.average),
            pointBackgroundColor: chartPointsBgColour,
            backgroundColor: chartReturningConnectionColour,
            borderColor: chartReturningConnectionBorderColour,
            fill: true,
            borderWidth: 1,
            tension: 0,
            hidden: !hasNonZeroData(
              processed_chart_data.map(bin => bin.guest.returning.average)
            ),
          },
          {
            label: "New",
            data: processed_chart_data.map(bin => bin.guest.new.average),
            pointBackgroundColor: chartPointsBgColour,
            backgroundColor: chartNewConnectionColour,
            borderColor: chartNewConnectionBorderColour,
            fill: true,
            borderWidth: 1,
            tension: 0,
            hidden: !hasNonZeroData(
              processed_chart_data.map(bin => bin.guest.new.average)
            ),
          },
        ]
        : displayBy === "age"
          ? [
            {
              label: "No response",
              data: processed_chart_data.map(b => b.age.no_response.average),
              // spanGaps: true,
              // showLine: true,
              pointBackgroundColor: chartPointsBgColour,
              backgroundColor: chartAgeNoResponseColour,
              borderColor: chartAgeNoResponseBorderColour,
              fill: true,
              borderWidth: 1,
              tension: 0,
              hidden: !hasNonZeroData(processed_chart_data.map(b => b.age.no_response.average)),
            },
            {
              label: "65+",
              data: processed_chart_data.map(b => b.age["65+"].average),
              // spanGaps: true,
              // showLine: true,
              pointBackgroundColor: chartPointsBgColour,
              backgroundColor: chartAgeOver65Colour,
              borderColor: chartAgeOver65BorderColour,
              fill: true,
              borderWidth: 1,
              tension: 0,
              hidden: !hasNonZeroData(processed_chart_data.map(b => b.age["65+"].average)),
            },
            {
              label: "55-64",
              data: processed_chart_data.map(b => b.age["55-64"].average),
              // spanGaps: true,
              // showLine: true,
              pointBackgroundColor: chartPointsBgColour,
              backgroundColor: chartAge5564Colour,
              borderColor: chartAge5564BorderColour,
              fill: true,
              borderWidth: 1,
              tension: 0,
              hidden: !hasNonZeroData(processed_chart_data.map(b => b.age["55-64"].average)),
            },
            {
              label: "45-54",
              data: processed_chart_data.map(b => b.age["45-54"].average),
              // spanGaps: true,
              // showLine: true,
              pointBackgroundColor: chartPointsBgColour,
              backgroundColor: chartAge4554Colour,
              borderColor: chartAge4554BorderColour,
              fill: true,
              borderWidth: 1,
              tension: 0,
              hidden: !hasNonZeroData(processed_chart_data.map(b => b.age["45-54"].average)),
            },
            {
              label: "35-44",
              data: processed_chart_data.map(b => b.age["35-44"].average),
              // spanGaps: true,
              // showLine: true,
              pointBackgroundColor: chartPointsBgColour,
              backgroundColor: chartAge3544Colour,
              borderColor: chartAge3544BorderColour,
              fill: true,
              borderWidth: 1,
              tension: 0,
              hidden: !hasNonZeroData(processed_chart_data.map(b => b.age["35-44"].average)),
            },
            {
              label: "25-34",
              data: processed_chart_data.map(b => b.age["25-34"].average),
              // spanGaps: true,
              // showLine: true,
              pointBackgroundColor: chartPointsBgColour,
              backgroundColor: chartAge2534Colour,
              borderColor: chartAge2534BorderColour,
              fill: true,
              borderWidth: 1,
              tension: 0,
              hidden: !hasNonZeroData(processed_chart_data.map(b => b.age["25-34"].average)),
            },
            {
              label: "18-24",
              data: processed_chart_data.map(b => b.age["18-24"].average),
              // spanGaps: true,
              // showLine: true,
              pointBackgroundColor: chartPointsBgColour,
              backgroundColor: chartAge1824Colour,
              borderColor: chartAge1824BorderColour,
              fill: true,
              borderWidth: 1,
              tension: 0,
              hidden: !hasNonZeroData(processed_chart_data.map(b => b.age["18-24"].average)),
            },
            {
              label: "Under 18",
              data: processed_chart_data.map(b => b.age["Under 18"].average),
              // spanGaps: true,
              // showLine: true,
              pointBackgroundColor: chartPointsBgColour,
              backgroundColor: chartAgeBelow18Colour,
              borderColor: chartAgeBelow18BorderColour,
              fill: true,
              borderWidth: 1,
              tension: 0,
              hidden: !hasNonZeroData(processed_chart_data.map(b => b.age["Under 18"].average)),
            },
          ]
          : displayBy === "gender"
            ? [
              {
                label: "No response",
                data: processed_chart_data.map((entry) => entry.gender.no_response.average),
                backgroundColor: chartGenderNoResponseColour,
                borderColor: chartGenderNoResponseBorderColour,
                fill: true,
                borderWidth: 1,
                tension: 0,
                pointBackgroundColor: chartPointsBgColour,
                hidden: !hasNonZeroData(processed_chart_data.map((entry) => entry.gender.no_response.average)),
              },
              {
                label: "Other",
                data: processed_chart_data.map((entry) => entry.gender.others.average),
                backgroundColor: chartGenderOtherColour,
                borderColor: chartGenderOtherBorderColour,
                fill: true,
                borderWidth: 1,
                tension: 0,
                pointBackgroundColor: chartPointsBgColour,
                hidden: !hasNonZeroData(processed_chart_data.map((entry) => entry.gender.others.average)),
              },
              {
                label: "Female",
                data: processed_chart_data.map((entry) => entry.gender.female.average),
                backgroundColor: chartGenderFemaleColour,
                borderColor: chartGenderFemaleBorderColour,
                fill: true,
                borderWidth: 1,
                tension: 0,
                pointBackgroundColor: chartPointsBgColour,
                hidden: !hasNonZeroData(processed_chart_data.map((entry) => entry.gender.female.average)),
              },
              {
                label: "Male",
                data: processed_chart_data.map((entry) => entry.gender.male.average),
                backgroundColor: chartGenderMaleColour,
                borderColor: chartGenderMaleBorderColour,
                fill: true,
                borderWidth: 1,
                tension: 0,
                pointBackgroundColor: chartPointsBgColour,
                hidden: !hasNonZeroData(processed_chart_data.map((entry) => entry.gender.male.average)),
              },
            ]
            : [{
              label: "Avg Duration (min)",
              data: processed_chart_data.map(bin => bin.average),
              backgroundColor: chartNeutralColour,
              borderColor: chartNeutralBorderColour,
              pointBackgroundColor: chartPointsBgColour,
              fill: true,
              borderWidth: 1,
              tension: 0,
            }],
  };

  const maxAvg = Math.max(0, ...processed_chart_data.map(b => b.average));
  const desiredTicks = 5;  // or however many horizontal lines you want
  let rawStep = maxAvg / desiredTicks;

  // round rawStep to nearest 5 or 10:
  let niceStep = Math.ceil(rawStep / 5) * 5;
  if (niceStep < 5) niceStep = 5;  // minimum granularity

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 800,
      onComplete: anim => {
        if (chartContainerRef && todayIndex !== -1) {
          const chart = anim.chart;
          const xAxis = chart.scales.x;
          const xPix = xAxis.getPixelForValue(todayIndex);
          const yPix = xAxis.top + xAxis.options.ticks.padding;
          setBadgePosition({
            left: xPix,
            top: yPix - 6,
            display: isCurrentDayInRange() ? "block" : "none",
          });
        }
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: "index",
        intersect: false,
        usePointStyle: true,
        itemSort: (a, b) => b.datasetIndex - a.datasetIndex,
        boxWidth: 6,
        callbacks: {
          title: function (tooltipItems) {
            const index = tooltipItems[0].dataIndex;
            return formatXAxisLabel(processed_chart_data[index].start_date);
          },
          label: function (context) {
            if (displayBy === "guest_status") {
              if (context.dataset.label === "New") {
                return `New: ${context.parsed.y} min`;
              } else if (context.dataset.label === "Returning") {
                return `Returning: ${context.parsed.y} min`;
              }
            } else if (displayBy === "age") {
              return `${context.dataset.label}: ${context.parsed.y} min`;
            } else if (displayBy === "gender") {
              return `${context.dataset.label}: ${context.parsed.y} min`;
            }
            return `Average: ${context.parsed.y} min`;
          },
          labelColor: function (context) {
            return {
              borderColor: context.dataset.borderColor,
              backgroundColor: context.dataset.borderColor,
            };
          },
          labelPointStyle: function (context) {
            return {
              pointStyle: "circle",
              rotation: 0,
            };
          },
        },
        bodyFont: {
          family: "Figtree",
        },
        titleFont: {
          family: "Figtree",
        },
      },
    },
    scales: {
      x: {
        type: "category",
        ticks: {
          color: fontColor,
          font: { family: "Figtree", size: 12 },
          padding: 8,
          callback: function (value, index, ticks) {
            return this.getLabelForValue(value);
          },
          maxRotation: 0,
        },
        grid: {
          display: true,
          color: gridColour,
          borderWidth: 1,
          drawTicks: false,
        },
        border: {
          dash: [2, 2],
        },
      },
      y: {
        stacked: true,
        position: "right",
        beginAtZero: false,
        ticks: {
          color: fontColor,
          padding: 8,
          stepSize: niceStep,
          max: Math.ceil(maxAvg / niceStep) * niceStep,
          callback: function (value) { return Number.isInteger(value) ? `${value} min` : null; },
          font: { family: "Figtree", size: 12 },
        },
        grid: {
          display: true,
          color: gridColour,
          borderWidth: 1,
          drawTicks: false,
        },
        border: {
          dash: [2, 2],
        },
      }
    }
  };

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }} ref={setChartContainerRef}>
      <Line data={chartData} options={options} style={{ width: "100%", height: "100%" }} />
      {todayIndex !== -1 && (
        <div
          style={{
            position: "absolute",
            left: `${badgePosition.left}px`,
            top: `${badgePosition.top}px`,
            transform: "translate(-50%, 0)",
            display: badgePosition.display,
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          <Badge variant="soft" size="1">
            {formatXAxisLabel(processed_chart_data[todayIndex]?.start_date)}
          </Badge>
        </div>
      )}
    </div>
  );
};